export const M3_UNIT_15 = {
  id: "m3-u15-want-would-like",
  title: "Unit 15: Want & Would Like",
  topic: "Master expressing desires, goals, and ambitions. Learn when to use want (casual) vs would like (formal). Essential for job interviews and life planning!",
  iconType: "target",
  grammarImage: "/m3-u15-want-would.jpg",
  grammarText: "Learn to express what you want in life! Understand want (informal) vs would like (formal), master contractions (I'd like), recognize slang (wanna), and integrate desires with your abilities. B1 level for real-life goal setting.",
  sets: [
    // ===================================================================
    // SET 1: Want + infinitive (básico) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set1",
      title: "Want + Infinitive",
      questions: [
        {
          type: "mc",
          q: "I _____ to learn English. It's important for my job!",
          options: ["want", "wants", "wanting"],
          a: "want",
          tip: "'Want' + infinitive (to + verb) = express desire!",
          successtip: "✓ WANT to learn! Basic desire structure! 📚 I/You/We/They = want! Foundation for all goals!"
        },
        {
          type: "mc",
          q: "She _____ to be a doctor. She studies very hard!",
          options: ["wants", "want", "wanting"],
          a: "wants",
          tip: "'Wants' for he/she/it! Third person singular!",
          successtip: "✓ She WANTS! Third person! 👩‍⚕️ He/She/It = wants (with S)! Career ambition!"
        },
        {
          type: "mc",
          q: "They _____ to buy a house. They're saving money!",
          options: ["want", "wants", "wanted"],
          a: "want",
          tip: "'Want' for plural subjects! They = want!",
          successtip: "✓ They WANT! Plural = no S! 🏠 Immigrant dream: house ownership! Saving = action!"
        },
        {
          type: "mc",
          q: "I want _____ work here. This is a good company!",
          options: ["to", "for", "at"],
          a: "to",
          tip: "Want + TO + base verb! Always 'to' before verb!",
          successtip: "✓ WANT TO work! Infinitive structure! 💼 Want + TO + verb = grammar rule! Job goal!"
        },
        {
          type: "mc",
          q: "My son _____ to go to college. Education is important!",
          options: ["wants", "want", "wanting"],
          a: "wants",
          tip: "'My son' = he = wants! Third person!",
          successtip: "✓ He WANTS! Son = singular! 🎓 Parent's dream: college education! Family ambition!"
        },
        {
          type: "mc",
          q: "We want _____ a better life for our kids.",
          options: ["to have", "have", "having"],
          a: "to have",
          tip: "Want + TO + verb! 'To have' = full infinitive!",
          successtip: "✓ WANT TO HAVE! Complete infinitive! 👨‍👩‍👧‍👦 Immigrant motivation: better life for children!"
        },
        {
          type: "mc",
          q: "I _____ to speak English fluently. I practice every day!",
          options: ["want", "wants", "wanting"],
          a: "want",
          tip: "'I want' for first person! Common learning goal!",
          successtip: "✓ I WANT! Personal goal! 🗣️ Fluency = ultimate English goal! Daily practice = dedication!"
        },
        {
          type: "mc",
          q: "She wants to _____ more money. She's looking for a second job.",
          options: ["earn", "earning", "earns"],
          a: "earn",
          tip: "After 'want to' = BASE FORM verb! No -ing, no -s!",
          successtip: "✓ EARN! Base form! 💰 Want to + BASE VERB! Second job = hustle reality!"
        },
        {
          type: "mc",
          q: "My parents _____ to visit me. They miss me a lot!",
          options: ["want", "wants", "wanting"],
          a: "want",
          tip: "'Parents' = they = want! Plural!",
          successtip: "✓ They WANT! Plural parents! 🛫 Family separation = immigrant struggle! Visits = precious!"
        },
        {
          type: "mc",
          q: "He wants _____ his own business. He's an entrepreneur!",
          options: ["to start", "start", "starting"],
          a: "to start",
          tip: "Want + TO + start! Full infinitive structure!",
          successtip: "✓ WANT TO START! Business dream! 🚀 Entrepreneurship = American dream! Own boss!"
        }
      ]
    },

    // ===================================================================
    // SET 2: Would like + infinitive (formal) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set2",
      title: "Would Like (Formal)",
      questions: [
        {
          type: "mc",
          q: "I _____ like to work here. This seems like a great company!",
          options: ["would", "will", "want"],
          a: "would",
          tip: "'Would like' = formal/polite version of 'want'!",
          successtip: "✓ WOULD like! Formal desire! 💼 Job interview language! Polite = professional!"
        },
        {
          type: "mc",
          q: "She _____ like to study medicine. It's her dream!",
          options: ["would", "will", "wants"],
          a: "would",
          tip: "'Would' is the same for all persons! No 'woulds'!",
          successtip: "✓ WOULD like! Same for everyone! 👩‍⚕️ Would = no person changes! Medical dream!"
        },
        {
          type: "mc",
          q: "I would like _____ for your company. I'm qualified!",
          options: ["to work", "work", "working"],
          a: "to work",
          tip: "Would like + TO + verb! Same structure as 'want to'!",
          successtip: "✓ WOULD LIKE TO work! Formal infinitive! 📋 Interview gold: would like + qualification!"
        },
        {
          type: "mc",
          q: "They would like _____ move to Canada. Better opportunities!",
          options: ["to", "for", "in"],
          a: "to",
          tip: "Would like + TO + base verb! Always 'to'!",
          successtip: "✓ Would like TO! Structure constant! 🍁 Immigration goal: Canada! Opportunities = hope!"
        },
        {
          type: "mc",
          q: "_____ you like to work on weekends? It pays more.",
          options: ["Would", "Do", "Are"],
          a: "Would",
          tip: "'Would you like' = polite question! Formal offer!",
          successtip: "✓ WOULD you like? Polite question! 💰 Weekend work = extra pay! Boss offering!"
        },
        {
          type: "mc",
          q: "We would like to _____ our English. Any suggestions?",
          options: ["improve", "improving", "improved"],
          a: "improve",
          tip: "After 'would like to' = BASE FORM verb!",
          successtip: "✓ IMPROVE! Base form! 📈 Would like to + base verb! Self-improvement goal!"
        },
        {
          type: "mc",
          q: "He _____ like to apply for the manager position.",
          options: ["would", "will", "wants"],
          a: "would",
          tip: "'Would like' for formal/professional situations!",
          successtip: "✓ WOULD like! Formal application! 📊 Career advancement = would like (not want)!"
        },
        {
          type: "mc",
          q: "I'd like _____ with the team. Teamwork is important!",
          options: ["to work", "work", "working"],
          a: "to work",
          tip: "'I'd like' = contraction of 'I would like'!",
          successtip: "✓ TO WORK! Contracted form! 👥 I'd = I would! Teamwork = valued skill!"
        },
        {
          type: "mc",
          q: "She'd like to _____ her family here eventually.",
          options: ["bring", "bringing", "brought"],
          a: "bring",
          tip: "'She'd like' = She would like! Then base form verb!",
          successtip: "✓ BRING! Base form after contraction! 👨‍👩‍👧‍👦 She'd = She would! Family reunion goal!"
        },
        {
          type: "mc",
          q: "_____ would like to schedule an interview with you.",
          options: ["I", "My", "Me"],
          a: "I",
          tip: "Subject pronoun before 'would like'! I, not me!",
          successtip: "✓ I would like! Subject form! 📅 Interview request = formal language! Professional!"
        }
      ]
    },

    // ===================================================================
    // SET 3: Want vs Would like (contraste) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set3",
      title: "Want vs Would Like",
      questions: [
        {
          type: "mc",
          q: "Casual with friends: 'I _____ pizza!' Formal at restaurant: 'I _____ like pizza, please.'",
          options: ["want / would", "would / want", "want / want"],
          a: "want / would",
          tip: "Want = casual! Would like = polite/formal!",
          successtip: "✓ WANT vs WOULD LIKE! Context matters! 🍕 Friends = want! Restaurant = would like! Politeness!"
        },
        {
          type: "mc",
          q: "Job interview question: 'Why _____ you _____ to work here?'",
          options: ["would / like", "do / want", "are / wanting"],
          a: "would / like",
          tip: "Job interviews = FORMAL! Use 'would like'!",
          successtip: "✓ WOULD LIKE! Interview language! 💼 Formal = would like! Professional setting!"
        },
        {
          type: "mc",
          q: "Talking to your brother: 'I _____ to go home. I'm tired!'",
          options: ["want", "would like", "'d like"],
          a: "want",
          tip: "Family/friends = casual! 'Want' is fine!",
          successtip: "✓ WANT! Family talk! 😴 Casual = want! Brother = informal! Direct expression!"
        },
        {
          type: "mc",
          q: "Email to boss: 'I _____ to request a day off next week.'",
          options: ["would like", "want", "wanna"],
          a: "would like",
          tip: "Boss/professional email = FORMAL! Would like!",
          successtip: "✓ WOULD LIKE! Professional email! 📧 Boss = formal! Would like shows respect!"
        },
        {
          type: "mc",
          q: "At a coffee shop: 'I _____ a latte, please.' (Polite order)",
          options: ["'d like", "want", "wants"],
          a: "'d like",
          tip: "Customer ordering = polite! 'I'd like' is respectful!",
          successtip: "✓ I'D LIKE! Customer politeness! ☕ Service = be polite! Contraction common!"
        },
        {
          type: "mc",
          q: "Kid asking parent: 'I _____ ice cream!' (Direct request)",
          options: ["want", "would like", "'d like"],
          a: "want",
          tip: "Kids = direct! 'Want' is natural for children!",
          successtip: "✓ WANT! Kid language! 🍦 Children = direct! No formality needed! Natural speech!"
        },
        {
          type: "mc",
          q: "Talking to manager: 'I _____ to discuss my schedule, please.'",
          options: ["would like", "want", "wanna"],
          a: "would like",
          tip: "Manager = authority! Be polite with 'would like'!",
          successtip: "✓ WOULD LIKE! Professional request! 📋 Authority = respect! Formal language!"
        },
        {
          type: "mc",
          q: "Between coworkers: 'Do you _____ to grab lunch together?'",
          options: ["want", "would like", "'d like"],
          a: "want",
          tip: "Coworkers (equal level) = casual! 'Want' is fine!",
          successtip: "✓ WANT! Coworker casual! 🍽️ Peers = informal! Want shows friendliness!"
        },
        {
          type: "mc",
          q: "Cover letter: 'I _____ to contribute to your team's success.'",
          options: ["would like", "want", "wanna"],
          a: "would like",
          tip: "Cover letter = VERY formal! Always 'would like'!",
          successtip: "✓ WOULD LIKE! Formal writing! ✉️ Job application = maximum politeness! Professional!"
        },
        {
          type: "mc",
          q: "Talking to yourself: 'I really _____ to sleep!' (Thinking out loud)",
          options: ["want", "would like", "'d like"],
          a: "want",
          tip: "Talking to yourself = most casual! 'Want' natural!",
          successtip: "✓ WANT! Self-talk! 💭 Personal thoughts = want! No need for formality!"
        }
      ]
    },

    // ===================================================================
    // SET 4: Contracciones (I'd, She'd, They'd) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set4",
      title: "Contractions (I'd, She'd)",
      questions: [
        {
          type: "mc",
          q: "I would like = _____",
          options: ["I'd like", "I'll like", "I've like"],
          a: "I'd like",
          tip: "'Would' contracts to 'd! I would = I'd!",
          successtip: "✓ I'D! Contraction! 📝 Would = 'd! Common in speech and writing!"
        },
        {
          type: "mc",
          q: "She _____ like to start next Monday.",
          options: ["'d", "'ll", "'s"],
          a: "'d",
          tip: "'She'd' = She would! Apostrophe + d!",
          successtip: "✓ SHE'D! She would! 📅 'd = would! Fast speech contraction!"
        },
        {
          type: "mc",
          q: "We'd like to _____ our business. It's growing!",
          options: ["expand", "expanding", "expanded"],
          a: "expand",
          tip: "'We'd' = We would! Then BASE FORM verb!",
          successtip: "✓ EXPAND! We'd + base form! 📈 We'd = We would! Business growth!"
        },
        {
          type: "mc",
          q: "They _____ like to move to a bigger apartment.",
          options: ["'d", "'re", "'ll"],
          a: "'d",
          tip: "'They'd' = They would! Apostrophe + d!",
          successtip: "✓ THEY'D! They would! 🏢 'd for all persons! Housing upgrade!"
        },
        {
          type: "mc",
          q: "He'd like _____ apply for citizenship. It's time!",
          options: ["to", "for", "at"],
          a: "to",
          tip: "'He'd like' = He would like! Then TO + verb!",
          successtip: "✓ TO! He'd like TO! 🇺🇸 Contraction doesn't change structure! Citizenship dream!"
        },
        {
          type: "mc",
          q: "I _____ like to learn to drive. I need a car!",
          options: ["'d", "'ll", "'m"],
          a: "'d",
          tip: "'I'd' = I would! Common contraction in speech!",
          successtip: "✓ I'D! Fast speech! 🚗 'd = would! Driving = independence!"
        },
        {
          type: "mc",
          q: "You'd like to _____ more hours? We can arrange that.",
          options: ["work", "working", "worked"],
          a: "work",
          tip: "'You'd like to' = You would like to! Base form after!",
          successtip: "✓ WORK! You'd like to + base! 💼 More hours = more money! Flexible boss!"
        },
        {
          type: "mc",
          q: "She'd _____ to change her schedule if possible.",
          options: ["like", "likes", "liking"],
          a: "like",
          tip: "'She'd' already includes 'would'! Just add 'like'!",
          successtip: "✓ LIKE! She'd = She would! 📆 No extra 's'! Schedule flexibility!"
        },
        {
          type: "mc",
          q: "Write the full form: 'I'd like coffee.' = '_____'",
          options: ["I would like coffee.", "I will like coffee.", "I had like coffee."],
          a: "I would like coffee.",
          tip: "'d = would (NOT will or had in this context)!",
          successtip: "✓ I WOULD like! Expanded form! ☕ 'd usually = would in desires! Coffee order!"
        },
        {
          type: "mc",
          q: "Recognize the slang: 'I wanna go home.' = '_____'",
          options: ["I want to go home.", "I would like to go home.", "I wanted to go home."],
          a: "I want to go home.",
          tip: "'Wanna' = want to! SLANG! Don't write it, but understand it!",
          successtip: "✓ WANNA = WANT TO! Street slang! 🗣️ Hear it everywhere! Write 'want to', recognize 'wanna'!"
        }
      ]
    },

    // ===================================================================
    // SET 5: Negativos WANT (don't/doesn't want) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set5",
      title: "Negatives: Don't Want",
      questions: [
        {
          type: "mc",
          q: "I _____ want to work today. I'm sick!",
          options: ["don't", "doesn't", "didn't"],
          a: "don't",
          tip: "'Don't want' = I/you/we/they! Present negative!",
          successtip: "✓ DON'T want! Present negative! 😷 Sick day = don't want to work! Valid reason!"
        },
        {
          type: "mc",
          q: "She _____ want to eat. She's not hungry.",
          options: ["doesn't", "don't", "didn't"],
          a: "doesn't",
          tip: "'Doesn't want' = he/she/it! Third person negative!",
          successtip: "✓ DOESN'T want! Third person! 🍽️ Not hungry = doesn't want to eat! Natural!"
        },
        {
          type: "mc",
          q: "They don't want to _____ their job. It pays well!",
          options: ["lose", "losing", "lost"],
          a: "lose",
          tip: "After 'don't want to' = BASE FORM verb!",
          successtip: "✓ LOSE! Base form! 💼 Don't want to + base! Good job = don't want to lose!"
        },
        {
          type: "mc",
          q: "I _____ want to go back to my country empty-handed.",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "'Don't' for I! Strong immigrant motivation!",
          successtip: "✓ DON'T want! Personal negative! 🎯 Immigrant determination! Success = must!"
        },
        {
          type: "mc",
          q: "He doesn't want _____ work overtime. He's tired.",
          options: ["to", "for", "at"],
          a: "to",
          tip: "Doesn't want + TO + verb! Structure stays same!",
          successtip: "✓ TO! Negative keeps structure! 😴 Overtime fatigue! Work-life balance!"
        },
        {
          type: "mc",
          q: "We _____ want to move. We like this neighborhood!",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "'Don't want' for we! Plural negative!",
          successtip: "✓ DON'T want! Plural! 🏘️ Happy where we are! Neighborhood = home!"
        },
        {
          type: "mc",
          q: "She doesn't want to _____ late. The boss gets angry!",
          options: ["be", "being", "been"],
          a: "be",
          tip: "Doesn't want to + BE! Base form of 'to be'!",
          successtip: "✓ BE! Infinitive form! ⏰ Punctuality matters! Angry boss = stress!"
        },
        {
          type: "mc",
          q: "I don't _____ to quit my job. I need the money!",
          options: ["want", "wants", "wanting"],
          a: "want",
          tip: "DON'T + base form verb! Don't WANT!",
          successtip: "✓ WANT! Base after don't! 💰 Financial necessity! Can't quit = bills!"
        },
        {
          type: "mc",
          q: "They _____ want to take the bus. It's too slow!",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "'Don't' for they! Plural negative!",
          successtip: "✓ DON'T! They = don't! 🚌 Slow bus frustration! Time = valuable!"
        },
        {
          type: "mc",
          q: "My son doesn't want _____ study. He's lazy!",
          options: ["to", "for", "with"],
          a: "to",
          tip: "Doesn't want + TO + study! Structure constant!",
          successtip: "✓ TO! Negative infinitive! 📚 Parent frustration! Motivation = challenge!"
        }
      ]
    },

    // ===================================================================
    // SET 6: Negativos WOULD LIKE (wouldn't) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set6",
      title: "Negatives: Wouldn't Like",
      questions: [
        {
          type: "mc",
          q: "I _____ like to work there. The pay is too low!",
          options: ["wouldn't", "won't", "don't"],
          a: "wouldn't",
          tip: "'Wouldn't like' = would not like! Formal negative!",
          successtip: "✓ WOULDN'T like! Formal refusal! 💰 Low pay = wouldn't accept! Job standards!"
        },
        {
          type: "mc",
          q: "She _____ like that job. It's not her field.",
          options: ["wouldn't", "won't", "doesn't"],
          a: "wouldn't",
          tip: "'Wouldn't' same for all persons! No 'woulds not'!",
          successtip: "✓ WOULDN'T like! Same for everyone! 🎯 Wrong field = wouldn't like! Career fit!"
        },
        {
          type: "mc",
          q: "I wouldn't like to _____ at night. It's dangerous!",
          options: ["work", "working", "worked"],
          a: "work",
          tip: "Wouldn't like to + BASE FORM verb!",
          successtip: "✓ WORK! Base form! 🌙 Night shift concerns! Safety = priority!"
        },
        {
          type: "mc",
          q: "They _____ like to live in a small apartment.",
          options: ["wouldn't", "won't", "don't"],
          a: "wouldn't",
          tip: "'Wouldn't like' = preference negative! Polite!",
          successtip: "✓ WOULDN'T like! Preference! 🏠 Space needs! Family = bigger place!"
        },
        {
          type: "mc",
          q: "He wouldn't _____ to change careers now. Too risky!",
          options: ["like", "likes", "liking"],
          a: "like",
          tip: "'Wouldn't' already includes negation! Just add 'like'!",
          successtip: "✓ LIKE! Wouldn't + like! 🎲 Career change = risky! Stability matters!"
        },
        {
          type: "mc",
          q: "I _____ like to go back without success. I have pride!",
          options: ["wouldn't", "won't", "don't"],
          a: "wouldn't",
          tip: "'Wouldn't like' = strong preference negative!",
          successtip: "✓ WOULDN'T! Immigrant pride! 💪 Success = must! Return with dignity!"
        },
        {
          type: "mc",
          q: "We wouldn't like _____ move again. We just settled!",
          options: ["to", "for", "at"],
          a: "to",
          tip: "Wouldn't like + TO + verb! Structure consistent!",
          successtip: "✓ TO! Structure stays! 📦 Moving fatigue! Just settled = want stability!"
        },
        {
          type: "mc",
          q: "She _____ like to work on weekends. Family time matters!",
          options: ["wouldn't", "won't", "doesn't"],
          a: "wouldn't",
          tip: "'Wouldn't like' shows preference! Polite refusal!",
          successtip: "✓ WOULDN'T! Work-life balance! 👨‍👩‍👧‍👦 Family > overtime! Boundaries!"
        },
        {
          type: "mc",
          q: "I wouldn't like _____ alone. I'm social!",
          options: ["to work", "work", "working"],
          a: "to work",
          tip: "Wouldn't like + TO + work! Full infinitive!",
          successtip: "✓ TO WORK! Complete structure! 👥 Teamwork preference! Social = better!"
        },
        {
          type: "mc",
          q: "They wouldn't _____ to take that risk. Too dangerous!",
          options: ["like", "likes", "liked"],
          a: "like",
          tip: "'Wouldn't like' = would not like! One word after!",
          successtip: "✓ LIKE! Simple addition! ⚠️ Risk avoidance! Safety first!"
        }
      ]
    },

    // ===================================================================
    // SET 7: Questions with WANT (Do you want...?) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set7",
      title: "Questions: Do You Want?",
      questions: [
        {
          type: "mc",
          q: "_____ you want to go home? You look tired!",
          options: ["Do", "Are", "Would"],
          a: "Do",
          tip: "'Do you want' = present simple question! Casual!",
          successtip: "✓ DO you want? Present simple! 😴 Casual question! Friend concern!"
        },
        {
          type: "mc",
          q: "What _____ you want to do after work?",
          options: ["do", "are", "would"],
          a: "do",
          tip: "'What do you want' = asking for desire! Informal!",
          successtip: "✓ WHAT DO? Casual plan! 🎯 After-work plans! Friend invitation!"
        },
        {
          type: "mc",
          q: "_____ she want to come with us?",
          options: ["Does", "Do", "Is"],
          a: "Does",
          tip: "'Does she want' = third person question!",
          successtip: "✓ DOES she? Third person! 👥 Group invitation! Include her?"
        },
        {
          type: "mc",
          q: "Do you want _____ eat? I'm ordering pizza!",
          options: ["to", "for", "at"],
          a: "to",
          tip: "Do you want + TO + verb! Question keeps structure!",
          successtip: "✓ TO! Structure constant! 🍕 Pizza offer! Casual food invite!"
        },
        {
          type: "mc",
          q: "What does he _____ to be when he grows up?",
          options: ["want", "wants", "wanted"],
          a: "want",
          tip: "After 'does' = BASE FORM! Does he WANT (not wants)!",
          successtip: "✓ WANT! Base after does! 👶 Kid's dream! Career ambition!"
        },
        {
          type: "mc",
          q: "_____ they want to stay longer? There's more food!",
          options: ["Do", "Does", "Are"],
          a: "Do",
          tip: "'Do they want' = plural question!",
          successtip: "✓ DO they? Plural! 🍽️ Party host! More food = stay longer?"
        },
        {
          type: "mc",
          q: "Do you want me _____ help you? I have time!",
          options: ["to", "for", "with"],
          a: "to",
          tip: "Want + person + TO + verb! Offer structure!",
          successtip: "✓ TO! Offer to help! 🤝 Kind gesture! Friend helping friend!"
        },
        {
          type: "mc",
          q: "What _____ we want to achieve this year?",
          options: ["do", "does", "are"],
          a: "do",
          tip: "'What do we want' = group goal question!",
          successtip: "✓ WHAT DO? Group planning! 🎯 Team goals! Collective ambition!"
        },
        {
          type: "mc",
          q: "Does she want _____ work overtime? Extra pay available!",
          options: ["to", "for", "at"],
          a: "to",
          tip: "Does + want + TO + verb! Question structure!",
          successtip: "✓ TO! Overtime offer! 💰 Extra pay = incentive! Boss asking!"
        },
        {
          type: "mc",
          q: "Do you want _____ or not? Make up your mind!",
          options: ["to", "it", "that"],
          a: "to",
          tip: "'Want to' can end sentence if verb understood!",
          successtip: "✓ TO! Implied verb! 🤔 Make decision! Want to (go/do it) implied!"
        }
      ]
    },

    // ===================================================================
    // SET 8: Questions with WOULD LIKE (Would you like...?) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set8",
      title: "Questions: Would You Like?",
      questions: [
        {
          type: "mc",
          q: "_____ you like to work for our company?",
          options: ["Would", "Do", "Are"],
          a: "Would",
          tip: "'Would you like' = polite/formal question! Interview!",
          successtip: "✓ WOULD you like? Formal question! 💼 Job interview! Professional!"
        },
        {
          type: "mc",
          q: "What _____ you like to order? Here's the menu.",
          options: ["would", "do", "are"],
          a: "would",
          tip: "'What would you like' = polite service question!",
          successtip: "✓ WHAT WOULD? Restaurant service! 🍽️ Waiter politeness! Customer respect!"
        },
        {
          type: "mc",
          q: "Would you like _____ sit down? Please, have a seat!",
          options: ["to", "for", "at"],
          a: "to",
          tip: "Would you like + TO + verb! Polite offer!",
          successtip: "✓ TO! Polite invitation! 🪑 Host courtesy! Make guest comfortable!"
        },
        {
          type: "mc",
          q: "_____ she like to schedule an interview?",
          options: ["Would", "Does", "Is"],
          a: "Would",
          tip: "'Would she like' = formal question about preference!",
          successtip: "✓ WOULD she? Professional question! 📅 Interview scheduling! HR speaking!"
        },
        {
          type: "mc",
          q: "Would you like coffee _____ tea? We have both!",
          options: ["or", "and", "but"],
          a: "or",
          tip: "'Or' = offering choice between options!",
          successtip: "✓ OR! Choice offered! ☕🍵 Hospitality! Guest preference!"
        },
        {
          type: "mc",
          q: "What position _____ you like to apply for?",
          options: ["would", "do", "are"],
          a: "would",
          tip: "'Would you like to apply' = formal job question!",
          successtip: "✓ WOULD! Career question! 📋 HR interviewing! Position interest!"
        },
        {
          type: "mc",
          q: "Would you like me _____ call you back later?",
          options: ["to", "for", "with"],
          a: "to",
          tip: "Would you like + person + TO + verb! Offer structure!",
          successtip: "✓ TO! Phone etiquette! 📞 Professional callback! Customer service!"
        },
        {
          type: "mc",
          q: "_____ they like to join our team? We're hiring!",
          options: ["Would", "Do", "Are"],
          a: "Would",
          tip: "'Would they like' = formal invitation question!",
          successtip: "✓ WOULD they? Job offer! 👥 Team invitation! Recruitment!"
        },
        {
          type: "mc",
          q: "Would you like _____ start next Monday or later?",
          options: ["to", "for", "at"],
          a: "to",
          tip: "Would you like to + START! Offering choice!",
          successtip: "✓ TO! Start date question! 📅 Flexibility offered! New job timing!"
        },
        {
          type: "mc",
          q: "How many hours _____ you like to work per week?",
          options: ["would", "do", "are"],
          a: "would",
          tip: "'How many would you like' = preference quantity!",
          successtip: "✓ WOULD! Hour preference! ⏰ Schedule flexibility! Work-life balance!"
        }
      ]
    },

    // ===================================================================
    // SET 9: Integration with BECAUSE (B1 reasoning) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set9",
      title: "Want + Because (B1)",
      questions: [
        {
          type: "mc",
          q: "I want to learn English _____ I need a better job.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains WHY you want something! Reason!",
          successtip: "✓ BECAUSE! Reason for desire! 📚 Want + BECAUSE = B1 reasoning! Job motivation!"
        },
        {
          type: "mc",
          q: "She wants to move _____ her city is very expensive.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows cause! Expensive = reason to move!",
          successtip: "✓ BECAUSE! Financial reason! 💰 High cost = want to move! Economic logic!"
        },
        {
          type: "mc",
          q: "I'd like to change careers _____ I'm not happy in my current job.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains desire! Unhappiness = reason!",
          successtip: "✓ BECAUSE! Emotional reason! 😔 Unhappy = want change! Career satisfaction!"
        },
        {
          type: "mc",
          q: "They want to buy a house _____ rent is getting too high.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows financial motivation! Rent = problem!",
          successtip: "✓ BECAUSE! Economic logic! 🏠 High rent = want to own! Investment!"
        },
        {
          type: "mc",
          q: "I want to bring my family here _____ I miss them.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains emotional desire! Missing = reason!",
          successtip: "✓ BECAUSE! Emotional reason! 👨‍👩‍👧‍👦 Separation pain! Family reunion goal!"
        },
        {
          type: "mc",
          q: "She'd like to work from home _____ she has small children.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows practical reason! Kids = need flexibility!",
          successtip: "✓ BECAUSE! Parent logic! 👶 Small kids = work from home! Childcare solution!"
        },
        {
          type: "mc",
          q: "I want to improve my English _____ I can get a promotion.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' = reason! Wait... this is actually SO! Result!",
          successtip: "✓ Trick question! Should be SO (result)! 📈 Better English = promotion! Think carefully!"
        },
        {
          type: "mc",
          q: "He doesn't want to work nights _____ he has young kids.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains WHY he doesn't want! Family reason!",
          successtip: "✓ BECAUSE! Family priority! 🌙 Night shift = miss kids! Parent struggle!"
        },
        {
          type: "mc",
          q: "We want to save money _____ we can buy a car.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows PURPOSE/RESULT! Save → buy car!",
          successtip: "✓ SO! Purpose! 🚗 Saving leads to buying! Cause → effect!"
        },
        {
          type: "mc",
          q: "I'd like to become a citizen _____ I want to vote.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' shows motivation! Voting = reason for citizenship!",
          successtip: "✓ BECAUSE! Civic motivation! 🗳️ Voting rights = citizenship goal! Democracy!"
        }
      ]
    },

    // ===================================================================
    // SET 10: Integration with SO/BUT (B1 complex) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u15-set10",
      title: "Want + So/But (B1)",
      questions: [
        {
          type: "mc",
          q: "I want to save money, _____ I work two jobs.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows ACTION taken! Want to save → work two jobs!",
          successtip: "✓ SO! Action result! 💪 Want = motivation, SO = action! Hustle reality!"
        },
        {
          type: "mc",
          q: "She wants to study, _____ she has to work full-time.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' shows CONTRAST! Wants ≠ reality! Conflict!",
          successtip: "✓ BUT! Reality conflict! 📚💼 Want study BUT must work! Common struggle!"
        },
        {
          type: "mc",
          q: "I'd like to buy a house, _____ I'm saving every month.",
          options: ["so", "but", "because"],
          a: "so",
          tip: "'So' shows what you DO because of desire! Action!",
          successtip: "✓ SO! Practical action! 🏠 Want house SO save money! Plan in action!"
        },
        {
          type: "mc",
          q: "They want to go on vacation, _____ they can't afford it.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = desire vs reality! Want ≠ can afford!",
          successtip: "✓ BUT! Financial reality! ✈️ Dream BUT no money! Life constraint!"
        },
        {
          type: "mc",
          q: "He wants to be healthy, _____ he exercises every day.",
          options: ["so", "but", "because"],
          a: "so",
          tip: "'So' = action taken toward goal! Want → exercise!",
          successtip: "✓ SO! Health action! 💪 Want health SO exercise! Commitment!"
        },
        {
          type: "mc",
          q: "I want to rest, _____ I have to work.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = desire conflicts with obligation! Want ≠ have to!",
          successtip: "✓ BUT! Obligation vs desire! 😴 Want rest BUT must work! Worker reality!"
        },
        {
          type: "mc",
          q: "She'd like to learn to drive, _____ she's taking classes.",
          options: ["so", "but", "because"],
          a: "so",
          tip: "'So' shows action! Want to drive → taking classes!",
          successtip: "✓ SO! Learning action! 🚗 Want skill SO take classes! Progress!"
        },
        {
          type: "mc",
          q: "We want to move, _____ we can't sell our house.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = barrier! Want to move BUT stuck!",
          successtip: "✓ BUT! Housing barrier! 🏠 Want change BUT market problem! Real estate!"
        },
        {
          type: "mc",
          q: "I don't want to be late, _____ I'm leaving now.",
          options: ["so", "but", "because"],
          a: "so",
          tip: "'So' = preventive action! Don't want late → leave now!",
          successtip: "✓ SO! Prevention! ⏰ Avoid late SO leave early! Time management!"
        },
        {
          type: "mc",
          q: "He wants to spend time with family, _____ work is very busy.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = work-life conflict! Want family BUT busy work!",
          successtip: "✓ BUT! Work-life tension! 👨‍👩‍👧‍👦 Want family BUT career demands! Balance struggle!"
 }
      ]
    }
  ],  

readingSet: {
  id: "m3-u15-reading",
  title: "Reading: Goals and Ambitions",
  category: "reading",

  questions: [
    {
      type: "passage",
      passage: `**The Future Nurse**

Maria wants to become a nurse because she enjoys helping people. She's studying every day and would like to work in a hospital once she finishes her degree. She is very dedicated to her dream.`
    },
    {
      type: "mc",
      q: "What is Maria's career goal?",
      options: ["To become a nurse", "To study every day", "To help people"],
      a: "To become a nurse",
      tip: "Busca la frase 'wants to become'. Esa es su meta final.",
      successtip: "✓ CAREER AMBITION!\nMaria has a clear goal: she wants to be a nurse. 👩‍⚕️"
    },
    {
      type: "mc",
      q: "Why does Maria want to pursue this specific career?",
      options: [
        "Because she enjoys helping people",
        "Because she studies every day",
        "Because she already works in a hospital"
      ],
      a: "Because she enjoys helping people",
      tip: "El conector 'Because' introduce la motivación detrás de su deseo.",
      successtip: "✓ MOTIVATION!\nHelping others is her main reason for becoming a nurse. ❤️"
    },
    {
      type: "passage",
      passage: `**Saving for a Home**

Carlos wants to buy a house, but he doesn't have enough money yet. He's working two jobs so he can save for a down payment. It is a lot of work, but he is determined to have his own place.`
    },
    {
      type: "mc",
      q: "What is Carlos's current problem?",
      options: [
        "He doesn't have enough money",
        "He works two jobs",
        "He wants a house"
      ],
      a: "He doesn't have enough money",
      tip: "El conector 'But' introduce el obstáculo o barrera financiera.",
      successtip: "✓ FINANCIAL BARRIER!\nHe wants a house, but money is the current obstacle. 💰"
    },
    {
      type: "mc",
      q: "What is Carlos doing to solve his problem?",
      options: [
        "Working two jobs to save",
        "Buying a house right now",
        "Waiting for the money to appear"
      ],
      a: "Working two jobs to save",
      tip: "El conector 'So' muestra la acción que él toma para solucionar el problema.",
      successtip: "✓ THE SOLUTION!\nHe is working hard to reach his goal. 💪"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Professional Application (preguntas 5-7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Job Application**

I would like to work for your company because I have five years of experience in customer service. I don't want to change industries because I'm good at what I do.`
    },
    {
      type: "mc",
      q: "What does the person want in this situation?",
      options: ["To work for the company", "To change industries", "To have more experience"],
      a: "To work for the company",
      successtip: "✓ JOB GOAL!\n'Would like to work' is a polite way to express a job desire. 💼"
    },
    {
      type: "mc",
      q: "Why doesn't the person want to change industries?",
      options: [
        "Because they're good at what they do",
        "Because they have experience",
        "Because they already work for the company"
      ],
      a: "Because they're good at what they do",
      successtip: "✓ CAREER CONFIDENCE!\nThey want to stay in the same field because of their skills. 🎯"
    },
    {
      type: "mc",
      q: "Is the language used by this person formal or casual?",
      options: ["Formal (job application)", "Casual (with friends)", "Mixed"],
      a: "Formal (job application)",
      tip: "'Would like' es mucho más formal y profesional que 'want'.",
      successtip: "✓ PROFESSIONAL TONE!\nThis language is perfect for a cover letter or interview. 👔"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Family Connection (preguntas 8-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Bringing Family Together**

I want to bring my family to the US, so I'm working hard and saving money. I don't want them to live far away anymore. I miss them every day.`
    },
    {
      type: "mc",
      q: "In the first sentence, which connector shows the result of a desire?",
      options: ["Want...so (desire→action)", "Want...and (two desires)", "Don't want...anymore"],
      a: "Want...so (desire→action)",
      successtip: "✓ CAUSE AND EFFECT!\nThe desire to bring his family leads to the action of working hard. 🔗"
    },
    {
      type: "mc",
      q: "How does the person express a strong emotion about their family?",
      options: [
        "Don't want them to live far away anymore",
        "I'm working hard",
        "I want to bring my family"
      ],
      a: "Don't want them to live far away anymore",
      successtip: "✓ EMOTIONAL DEPTH!\n'Not... anymore' expresses a strong wish to change a painful situation. 😔"
    },
    {
      type: "mc",
      q: "How many different goals were expressed in all the readings combined?",
      options: [
        "Five (nurse, house, job, bring family, stay in industry)",
        "Three",
        "Seven"
      ],
      a: "Five (nurse, house, job, bring family, stay in industry)",
      successtip: "✓ READING COMPLETE!\nExcellent! You identified all the ambitions in the text. 🏆🎉"
    }
  ]
},
    SetReview: {
      id: "m3-u15-review",
      title: "Unit 15 Review",
      questions: [
        {
          type: "match",
          q: "Final Review: Match structures to contexts",
          pairs: [
            { left: "Want to (casual)", right: "Friends, family, self-talk" },
            { left: "Would like to (formal)", right: "Job interviews, bosses, customers" },
            { left: "Don't want to", right: "Negative desire/refusal" }
          ],
          a: "completed",
          tip: "Context determines formality!",
          successtip: "✓ Context mastered! 🎯 Want = casual! Would like = formal! Choose wisely!"
        },
        {
          type: "mc",
          q: "Job interview answer: 'I _____ to join your team because I have the right skills.'",
          options: ["would like", "want", "wanna"],
          a: "would like",
          tip: "Interview = always formal!",
          successtip: "✓ WOULD LIKE! Professional language! 💼 Formal = hired!"
        },
        {
          type: "mc",
          q: "Talking to friend: 'I _____ to go home. I'm tired!'",
          options: ["want", "would like", "'d like"],
          a: "want",
          tip: "Friend = casual! Direct 'want' is fine!",
          successtip: "✓ WANT! Casual with friends! 😴 Direct = friendship!"
        },
        {
          type: "mc",
          q: "Contraction: 'I would like' = '_____'",
          options: ["I'd like", "I'll like", "I've like"],
          a: "I'd like",
          tip: "'Would' contracts to 'd! Remember this!",
          successtip: "✓ I'D! Contraction! 📝 'd = would! Common in speech!"
        },
        {
          type: "mc",
          q: "Slang recognition: 'I wanna go!' means '_____'",
          options: ["I want to go!", "I would like to go!", "I wanted to go!"],
          a: "I want to go!",
          tip: "'Wanna' = want to! Recognize, don't write!",
          successtip: "✓ WANT TO! Slang! 🗣️ Wanna = street talk! Understand it!"
        },
        {
          type: "mc",
          q: "I want to learn English _____ I need a better job.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' = reason! Why you want it!",
          successtip: "✓ BECAUSE! Reason! 🧠 Want + BECAUSE = B1 logic!"
        },
        {
          type: "mc",
          q: "I want to save money, _____ I work two jobs.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' = action/result! What you do about it!",
          successtip: "✓ SO! Action! 💪 Want → SO action! Consequence!"
        },
        {
          type: "mc",
          q: "She wants to study, _____ she has to work.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = conflict! Want ≠ reality!",
          successtip: "✓ BUT! Conflict! 😔 Desire vs obligation! Real struggle!"
        },
        {
          type: "input",
          q: "Complete the immigrant dream: 'I _____ to bring my family here.'",
          a: "want",
          accept: { ignoreCase: true },
          tip: "'Want' for personal goals! Family reunion!",
          successtip: "✓ WANT! Family goal! 👨‍👩‍👧‍👦 Universal immigrant dream!"
        },
        {
          type: "mc",
          q: "Complete B1 sentence: 'I want to be a chef _____ I'm good at cooking, _____ I'm taking classes.'",
          options: ["because / so", "so / because", "but / so"],
          a: "because / so",
          tip: "Reason (because) → Action (so)! Two connectors!",
          successtip: "✓ BECAUSE + SO! B1 mastery! 🔥 Reason + action! Unit 15 complete! You can express any goal now! 🎯🎉"
        }
      ]
    },
  
  ReviewSet: {
      id: "m3-u15-set11",
      title: "Real-Life Goals",
      questions: [
        {
          type: "mc",
          q: "Job interview: 'I _____ to contribute to your company's success because I have experience in this field.'",
          options: ["would like", "want", "wanna"],
          a: "would like",
          tip: "Interview = FORMAL! Always 'would like'!",
          successtip: "✓ WOULD LIKE! Interview language! 💼 Formal + experience = professional! HIRED!"
        },
        {
          type: "mc",
          q: "Immigration dream: 'I want to _____ my parents here so we can be together.'",
          options: ["bring", "bringing", "brought"],
          a: "bring",
          tip: "Want to + BASE FORM! Family reunion goal!",
          successtip: "✓ BRING! Family reunion! 👨‍👩‍👦 Universal immigrant dream! Togetherness!"
        },
        {
          type: "mc",
          q: "Career goal: 'I'd like to _____ a nurse because I enjoy helping people.'",
          options: ["become", "becoming", "became"],
          a: "become",
          tip: "'I'd like to' = I would like to + BASE FORM!",
          successtip: "✓ BECOME! Career ambition! 👩‍⚕️ Helping profession! Noble goal!"
        },
        {
          type: "mc",
          q: "Honest reality: 'I want to rest, _____ I can't because bills don't stop.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = desire vs reality! Want rest BUT bills!",
          successtip: "✓ BUT! Immigrant grind! 💰 Want rest BUT financial pressure! Real struggle!"
        },
        {
          type: "mc",
          q: "Learning goal: 'I want to speak English fluently, _____ I practice every single day.'",
          options: ["so", "but", "because"],
          a: "so",
          tip: "'So' = action taken! Want fluency → practice daily!",
          successtip: "✓ SO! Dedication! 📚 Goal → daily action! Commitment pays!"
        },
        {
          type: "mc",
          q: "Housing dream: 'I'd like to buy a house, but I _____ enough for the down payment yet.'",
          options: ["don't have", "doesn't have", "didn't have"],
          a: "don't have",
          tip: "'Don't have' = present reality! Not enough yet!",
          successtip: "✓ DON'T HAVE! Financial reality! 🏠 Dream vs savings! Working toward it!"
        },
        {
          type: "mc",
          q: "Work preference: 'I _____ to work on weekends because I want time with my family.'",
          options: ["don't want", "doesn't want", "wouldn't like"],
          a: "don't want",
          tip: "'Don't want' = direct statement! Family priority!",
          successtip: "✓ DON'T WANT! Work-life balance! 👨‍👩‍👧‍👦 Weekends = family! Boundaries!"
        },
        {
          type: "mc",
          q: "Business ambition: 'I want to start my own business, _____ I need to save capital first.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = dream vs current step! Want business BUT need capital!",
          successtip: "✓ BUT! Entrepreneurial reality! 🚀 Big dream BUT practical steps! Patient planning!"
        },
        {
          type: "mc",
          q: "Citizenship goal: 'I _____ like to become a citizen so I can vote and travel freely.'",
          options: ["would", "will", "want"],
          a: "would",
          tip: "'Would like' for formal goal! Citizenship is official!",
          successtip: "✓ WOULD like! Formal ambition! 🇺🇸 Citizenship = rights! Civic participation!"
        },
        {
          type: "mc",
          q: "Motivational truth: 'I _____ want to go back to my country empty-handed. Success is a must!'",
          options: ["don't", "doesn't", "wouldn't"],
          a: "don't",
          tip: "'Don't want' = strong determination! No failure!",
          successtip: "✓ DON'T WANT! Immigrant pride! 💪 Return with success! Honor and dignity!"
        }
      ]
    }
};
